import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { RouteMap } from "./RouteMap";
import { api, formatApiError } from "@/lib/api";
import { scrollToId } from "@/lib/site";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator, MapPin } from "lucide-react";

const CONDITION = {
    waldfrisch: { label: "Waldfrisch", hint: "frisch geschlagen, zum Selbsttrocknen", adjust: 0 },
    trocken: { label: "Ofenfertig trocken", hint: "komplett getrocknet — nur auf Anfrage", adjust: null },
};
const LENGTHS = ["20", "25", "30", "33", "40", "50", "60"];

export const Preise = () => {
    const [prices, setPrices] = useState([]);
    const [wood, setWood] = useState("");
    const [qty, setQty] = useState([8]);
    const [length, setLength] = useState("33");
    const [condition, setCondition] = useState("waldfrisch");
    const [km, setKm] = useState([20]);
    const [ortInput, setOrtInput] = useState("");
    const [distResult, setDistResult] = useState(null);
    const [checking, setChecking] = useState(false);

    const checkOrt = async () => {
        if (!ortInput.trim()) return;
        setChecking(true);
        try {
            const r = await api.get(`/distance?ort=${encodeURIComponent(ortInput.trim())}`);
            setDistResult(r.data);
            setKm([Math.max(0, Math.min(150, r.data.km))]);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setChecking(false);
        }
    };

    useEffect(() => {
        api.get("/prices").then((r) => {
            setPrices(r.data);
            const first = r.data.find((p) => p.category === "Brennholz" && p.price != null);
            if (first) setWood(first.id);
        }).catch(() => {});
    }, []);

    const firewood = prices.filter((p) => p.category === "Brennholz" && p.price != null);
    const selected = prices.find((p) => p.id === wood);
    const onRequest = condition === "trocken";

    const calc = useMemo(() => {
        if (!selected || onRequest) return null;
        const perSrm = Math.max(35, selected.price + CONDITION[condition].adjust);
        const fee = Math.round(km[0] * 1.5);
        return { perSrm, woodTotal: perSrm * qty[0], fee, total: perSrm * qty[0] + fee };
    }, [selected, qty, condition, km, onRequest]);

    const sendInquiry = () => {
        const lines = [
            `${qty[0]} SRM ${selected?.name || "Brennholz"} — ${length} cm, ${CONDITION[condition].label}`,
            distResult ? `Lieferung nach ${distResult.label} (ca. ${km[0]} km ab Templin)` : `Entfernung ab Templin: ca. ${km[0]} km`,
            calc ? `Richtwert laut Rechner: ${calc.total.toFixed(0)} €` : "Bitte um ein individuelles Angebot.",
        ];
        window.dispatchEvent(
            new CustomEvent("contact:prefill", {
                detail: {
                    topic: "Brennholz",
                    message: `Anfrage über den Richtwert-Rechner:\n${lines.join("\n")}`,
                    ...(distResult ? { city: distResult.label } : {}),
                },
            })
        );
        scrollToId("kontakt");
    };

    return (
        <section id="preise" className="border-y border-[#2E2517] bg-[#1B150E]" data-testid="preise-section">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 py-24 lg:grid-cols-2 lg:py-32">
                <Reveal>
                    <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#C88A45]">Preisliste</p>
                    <h2 className="mb-8 font-display text-4xl font-extrabold uppercase tracking-tight text-[#EDE6DA] sm:text-5xl">
                        Ehrliche Preise, <span className="text-[#C88A45]">klar aufgelistet.</span>
                    </h2>
                    <p className="mb-10 max-w-md text-sm leading-relaxed text-[#9C917D]">
                        Der endgültige Preis hängt von Baumart, Trocknung und Entfernung ab — alle Richtwerte
                        finden Sie hier, Ihr individuelles Angebot erhalten Sie auf Anfrage. Bei Abnahme
                        größerer Mengen ist ein Preisnachlass möglich.
                    </p>
                    <div data-testid="price-list" className="divide-y divide-[#2E2517] border-y border-[#2E2517]">
                        {prices.map((p) => (
                            <div key={p.id} className="flex items-baseline justify-between gap-4 py-4" data-testid={`price-row-${p.id}`}>
                                <div>
                                    <p className="font-semibold text-[#EDE6DA]">{p.name}</p>
                                    {p.note && <p className="mt-0.5 text-xs text-[#9C917D]">{p.note}</p>}
                                </div>
                                <p className="shrink-0 font-display text-xl font-bold text-[#C88A45]">
                                    {p.price != null ? `ab ${p.price.toFixed(0)} ${p.unit}` : p.unit}
                                </p>
                            </div>
                        ))}
                    </div>
                </Reveal>

                <Reveal delay={0.15}>
                    <div className="rounded border border-[#2E2517] bg-[#120E09] p-8 lg:sticky lg:top-28" data-testid="price-calculator">
                        <div className="mb-8 flex items-center gap-3">
                            <Calculator size={20} className="text-[#C88A45]" />
                            <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-[#EDE6DA]">Richtwert-Rechner</h3>
                        </div>

                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Baumart</label>
                        <Select value={wood} onValueChange={setWood}>
                            <SelectTrigger data-testid="calc-wood-select" className="mb-6 border-[#2E2517] bg-[#1B150E] text-[#EDE6DA]">
                                <SelectValue placeholder="Baumart wählen" />
                            </SelectTrigger>
                            <SelectContent className="border-[#2E2517] bg-[#1B150E] text-[#EDE6DA]">
                                {firewood.map((p) => (
                                    <SelectItem key={p.id} value={p.id} data-testid={`calc-wood-option-${p.id}`}>
                                        {p.name} — ab {p.price.toFixed(0)} €/SRM
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Scheitlänge</label>
                        <Select value={length} onValueChange={setLength}>
                            <SelectTrigger data-testid="calc-length-select" className="mb-6 border-[#2E2517] bg-[#1B150E] text-[#EDE6DA]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="border-[#2E2517] bg-[#1B150E] text-[#EDE6DA]">
                                {LENGTHS.map((l) => (
                                    <SelectItem key={l} value={l} data-testid={`calc-length-${l}`}>
                                        {l} cm
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">
                            Menge: {qty[0]} Schüttraummeter
                        </label>
                        <Slider
                            data-testid="calc-qty-slider"
                            value={qty}
                            onValueChange={setQty}
                            min={8}
                            max={30}
                            step={1}
                            className="mb-2"
                        />
                        <p className="mb-6 text-xs text-[#9C917D]" data-testid="calc-min-hint">
                            Mindestabnahme 8 SRM — kleinere Mengen nur bei sehr kurzer Anfahrt, einfach anfragen.
                        </p>

                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Zustand</label>
                        <Select value={condition} onValueChange={setCondition}>
                            <SelectTrigger data-testid="calc-condition-select" className="mb-6 border-[#2E2517] bg-[#1B150E] text-[#EDE6DA]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="border-[#2E2517] bg-[#1B150E] text-[#EDE6DA]">
                                {Object.entries(CONDITION).map(([k, v]) => (
                                    <SelectItem key={k} value={k} data-testid={`calc-condition-${k}`}>
                                        {v.label} — {v.hint}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">
                            Entfernung: {km[0]} km {distResult && <span className="normal-case tracking-normal text-[#C88A45]">· {distResult.label}</span>}
                        </label>
                        <div className="mb-3 flex gap-2">
                            <Input
                                data-testid="calc-ort-input"
                                value={ortInput}
                                onChange={(e) => setOrtInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && checkOrt()}
                                placeholder="Ihr Ort oder PLZ"
                                className="border-[#2E2517] bg-[#1B150E] text-[#EDE6DA]"
                            />
                            <button
                                data-testid="calc-ort-button"
                                onClick={checkOrt}
                                disabled={checking}
                                className="flex shrink-0 items-center gap-1.5 rounded-full border border-[#C88A45]/50 px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#C88A45] transition-colors duration-200 hover:bg-[#C88A45]/10 disabled:opacity-50"
                            >
                                <MapPin size={13} /> {checking ? "…" : "Prüfen"}
                            </button>
                        </div>
                        <Slider
                            data-testid="calc-km-slider"
                            value={km}
                            onValueChange={(v) => { setKm(v); setDistResult(null); }}
                            min={0}
                            max={150}
                            step={5}
                            className="mb-2"
                        />
                        <p className="mb-4 text-xs text-[#9C917D]" data-testid="calc-distance-hint">
                            Lieferung ab Templin — bis ca. 150 km Entfernung. Ort eingeben oder Regler schieben.
                        </p>
                        {distResult && (
                            <div className="mb-6 overflow-hidden rounded border border-[#2E2517]" data-testid="calc-map">
                                <RouteMap dest={{ lat: distResult.lat, lon: distResult.lon, label: distResult.label }} route={distResult.route} />
                            </div>
                        )}

                        <div className="border-t border-[#2E2517] pt-6" data-testid="calc-result">
                            {calc ? (
                                <>
                                    <div className="flex justify-between text-sm text-[#9C917D]">
                                        <span>{qty[0]} SRM × {calc.perSrm.toFixed(0)} € · {length} cm</span>
                                        <span>{calc.woodTotal.toFixed(0)} €</span>
                                    </div>
                                    <div className="flex justify-between text-sm text-[#9C917D]">
                                        <span>Anfahrt & Lieferung ({km[0]} km)</span>
                                        <span>{calc.fee} €</span>
                                    </div>
                                    <div className="mt-4 flex items-baseline justify-between">
                                        <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#EDE6DA]">Richtwert</span>
                                        <span className="font-display text-5xl font-black text-[#C88A45]" data-testid="calc-total">
                                            {calc.total.toFixed(0)} €
                                        </span>
                                    </div>
                                    <p className="mt-3 text-xs text-[#9C917D]">
                                        Unverbindlicher Richtwert — bei Abnahme größerer Mengen ist ein
                                        Preisnachlass möglich. Ihr genaues Angebot erhalten Sie auf Anfrage.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <div className="flex items-baseline justify-between">
                                        <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#EDE6DA]">Preis</span>
                                        <span className="font-display text-4xl font-black text-[#C88A45]" data-testid="calc-total">
                                            Auf Anfrage
                                        </span>
                                    </div>
                                    <p className="mt-3 text-xs text-[#9C917D]">
                                        Komplett getrocknetes Holz kalkuliere ich individuell für Sie — schreiben
                                        Sie mir einfach über das Formular oder rufen Sie an.
                                    </p>
                                </>
                            )}
                        </div>

                        <button
                            data-testid="calc-inquiry-button"
                            onClick={sendInquiry}
                            className="mt-8 w-full rounded-full bg-[#C88A45] py-3 text-sm font-bold uppercase tracking-wide text-[#120E09] transition-[background-color,transform] duration-200 hover:bg-[#DBA057] active:scale-95"
                        >
                            Anfrage mit diesen Angaben senden
                        </button>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
